import React from 'react'
import { gsap } from 'gsap'
import { uniqueId } from 'lodash-es'
import { observer } from 'mobx-react-lite'

interface IProps {
  className?: string
  style?: any
  trigger: (open: () => Promise<unknown>) => React.ReactElement
  render: (close: () => Promise<unknown>) => React.ReactElement
}

const Dropdown = observer((props: IProps) => {
  const innerRef = React.useRef(null)
  const [id] = React.useState(uniqueId('dropdown-'))

  const open = async (): Promise<unknown> =>
    await new Promise((resolve) => {
      const inner = innerRef.current
      if (!inner) {
        resolve({})
        return
      }

      const tl = gsap.timeline({
        onComplete: () => {
          resolve({})
          document.addEventListener('click', clickHandler, { once: true })
          document.addEventListener('keyup', keyupHandler, { once: true })
        }
      })
      tl.set(inner, { willChange: 'transform, opacity', display: 'block' })
      tl.to(inner, { y: 0, opacity: 1, duration: 0.2 }, '>0.15')
      tl.set(inner, { willChange: 'auto' })
    })

  const close = async (): Promise<unknown> =>
    await new Promise((resolve) => {
      const inner = innerRef.current
      if (!inner) {
        resolve({})

        return
      }

      const tl = gsap.timeline({
        onComplete: () => {
          document.removeEventListener('click', clickHandler)
          document.removeEventListener('keyup', keyupHandler)
          resolve({})
        }
      })
      tl.set(inner, { willChange: 'transform, opacity' })
      tl.to(inner, { y: 15, opacity: 0, duration: 0.2 }, '>0.15')
      tl.set(inner, { willChange: 'auto', display: 'none' })
    })

  const clickHandler = async (e: any): Promise<void> => {
    if (!(e.target as HTMLElement).closest(`#${id} .dropdown__inner`)) {
      await close()
    }
  }

  const keyupHandler = async (e: any): Promise<void> => {
    if (e.key === 'Escape') {
      await close()
    }
  }

  React.useEffect(() => {
    return (): void => {
      document.removeEventListener('click', clickHandler)
      document.removeEventListener('keyup', keyupHandler)
    }
  }, [])

  return (
    <div
      style={props.style}
      className={`dropdown ${props.className ?? ''}`.trim()}
      id={id}
    >
      {props.trigger(open)}
      <div ref={innerRef} className="dropdown__inner">
        {props.render(close)}
      </div>
    </div>
  )
})

export default Dropdown
